import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  forwardRef,
  inject,
  Input,
  OnChanges,
  Output,
  SimpleChanges
} from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR, UntypedFormGroup, Validators } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { I18NextPipe } from 'angular-i18next';
import { MbsValidators } from 'mbs-ui-kit/utils/mbs-validators';
import { AuthFacade } from 'mbs-ui-shared@facades/auth.facade';
import { isConfirmPasswordValidator } from 'mbs-ui-shared@utils/validators';
import { noop, Subscription } from 'rxjs';
import { ConfirmPassData } from './confirm-pass-model';

import { FormGroupComponent, InputComponent } from 'mbs-ui-kit';

import { CommonModule } from '@angular/common';

import { ReactiveFormsModule } from '@angular/forms';

import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';

const ConfirmPasswordValueAccessor: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => ConfirmPasswordComponent),
  multi: true
};

/**
 * Author: Roman Sh.
 * Block with two password controls with confirm validator and password rules async validator
 * */
@UntilDestroy()
@Component({
  selector: 'mbs-confirm-password',
  templateUrl: './confirm-password.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ConfirmPasswordValueAccessor],
  imports: [CommonModule, FormGroupComponent, InputComponent, NgbTooltipModule, ReactiveFormsModule]
})
export class ConfirmPasswordComponent implements ControlValueAccessor, OnChanges {
  i18nPipe = inject(I18NextPipe);
  private auth = inject(AuthFacade);

  /* Main inputs section */
  @Input() boldLabel = true;
  @Input() initialPassword = '';
  @Input() disabled = false;
  @Input() needValidateOnMount = false;
  @Input() forceValidate = false;
  @Input() disabledButtons = false;
  @Input() label = '';
  @Input() confirmPasswordLabel = this.i18nPipe.transform('wizards:confirm_password_label');
  @Input() placeholder = '';
  @Input() confirmPlaceholder = '';
  @Input() id = 'confirmPassComponentPassword';
  @Input() fakePassword = '';
  @Input() showRequiredMark = true;

  @Input() hideAsyncValidator = false;

  /* Error in tooltip inputs section */
  @Input() showErrorsInTooltip = false;
  @Input() errorsTooltipContainer = 'body';
  @Input() errorsTooltipPlacement = 'auto';
  @Input() errorsTooltipClass = 'tooltip-xl';
  @Input() errorTooltipTitle = '';

  @Output() valueChange = new EventEmitter<boolean>();

  public readonly elementSelectors = {
    name: {
      password: 'confirm-password_password',
      passwordConfirm: 'confirm-password_password-confirm'
    }
  };

  public onChange: (value: unknown) => void;
  public onTouched: () => void;

  public passwordInputType = 'password';
  public confirmPasswordInputType = 'password';
  public passwordRules: string[] = [];
  public passwordForm = new UntypedFormGroup(
    {
      password: new FormControl('', {
        validators: [Validators.required],
        asyncValidators: [MbsValidators.passwordStrengthValidator(this.auth, this.fakePassword)],
        updateOn: 'change'
      }),
      confirmationPassword: new FormControl('', [Validators.required])
    },
    isConfirmPasswordValidator.bind(null, { password: 'password', confirmPassword: 'confirmationPassword' })
  );

  private statusSubscribe$: Subscription;
  private valueSubscribe$: Subscription;
  /* Value accessor data section */
  private myValue: ConfirmPassData = { password: '', confirmationPassword: '', valid: false };

  set value(value: ConfirmPassData) {
    this.myValue = value;
    this.notifyValueChange();
  }

  get value(): ConfirmPassData {
    return this.myValue;
  }

  constructor() {
    this.auth
      .getPasswordRules()
      .pipe(untilDestroyed(this))
      .subscribe((data) => (this.passwordRules = data));

    this.initSubscribes();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.hideAsyncValidator?.currentValue) this.resetFormWithoutAsyncValidator();

    if (changes.disabled) {
      if (changes.disabled.currentValue) {
        this.passwordForm.get('password').disable();
        this.passwordForm.get('confirmationPassword').disable();
      } else {
        this.passwordForm.get('password').enable();
        this.passwordForm.get('confirmationPassword').enable();
      }
    }

    if (changes.fakePassword?.currentValue && !this.hideAsyncValidator) {
      this.passwordForm.get('password').setAsyncValidators(MbsValidators.passwordStrengthValidator(this.auth, this.fakePassword));
    }

    if (changes.forceValidate?.currentValue) {
      this.passwordForm.get('password').markAsDirty();
      this.passwordForm.get('password').updateValueAndValidity();
      this.passwordForm.get('confirmationPassword').markAsDirty();
      this.passwordForm.get('confirmationPassword').updateValueAndValidity();
    }

    if (changes.needValidateOnMount?.currentValue) {
      this.passwordForm.markAllAsTouched();
      this.passwordForm.updateValueAndValidity();
    }
  }

  changeTypePassword(): void {
    this.passwordInputType = this.passwordInputType === 'password' ? 'text' : 'password';
  }

  changeTypeConfirmationPassword(): void {
    this.confirmPasswordInputType = this.confirmPasswordInputType === 'password' ? 'text' : 'password';
  }

  passwordFocusHandler(event, isConfirmation = false): void {
    if (event?.target?.value && this.initialPassword && event.target.value === this.initialPassword) {
      this.passwordForm.get(isConfirmation ? 'confirmationPassword' : 'password').setValue('');
    }
  }

  passwordBlurHandler(event, isConfirmation = false): void {
    if (!event?.target?.value && this.initialPassword) {
      this.passwordForm.get(isConfirmation ? 'confirmationPassword' : 'password').setValue(this.initialPassword);
    }
  }

  notifyValueChange(): void {
    if (this.onChange) {
      this.onChange(this.value);
    }
  }

  writeValue(value: ConfirmPassData): void {
    this.myValue = value;
    this.updateForm(value);
  }

  updateForm(value: ConfirmPassData): void {
    if (!value) return;

    this.passwordForm.reset({ password: value.password, confirmationPassword: value.confirmationPassword });

    if (this.needValidateOnMount) {
      this.passwordForm.markAllAsTouched();
      this.passwordForm.updateValueAndValidity();
    }
  }

  registerOnChange(fn: (data: unknown) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  private initSubscribes(): void {
    if (this.statusSubscribe$) this.statusSubscribe$.unsubscribe();
    if (this.valueSubscribe$) this.valueSubscribe$.unsubscribe();

    let isPending = false;
    this.statusSubscribe$ = this.passwordForm.statusChanges.pipe(untilDestroyed(this)).subscribe({
      next: (status) => {
        const pending = status === 'PENDING';

        if (isPending && !pending) {
          isPending = pending;
          return void this.passwordForm.updateValueAndValidity();
        }

        isPending = pending;
      }
    });

    this.valueSubscribe$ = this.passwordForm.valueChanges.pipe(untilDestroyed(this)).subscribe({
      next: ({ password, confirmationPassword }) => {
        this.valueChange.emit(true);
        this.value = {
          password,
          confirmationPassword,
          valid:
            this.passwordForm.valid ||
            (!this.passwordForm.get('password').pending &&
              !this.passwordForm.get('password').errors &&
              !this.passwordForm.get('confirmationPassword').errors)
        };
      },
      error: noop
    });
  }

  private resetFormWithoutAsyncValidator(): void {
    this.passwordForm = new UntypedFormGroup(
      {
        password: new FormControl(this.passwordForm.get('password').value, Validators.required),
        confirmationPassword: new FormControl(this.passwordForm.get('confirmationPassword').value, [Validators.required])
      },
      isConfirmPasswordValidator.bind(null, { password: 'password', confirmPassword: 'confirmationPassword' })
    );
    this.initSubscribes();
  }
}
